import AgTechGrid from "@agtech/web/components/Grids/AgTechGrid";
import { AgTechGridHeaderConfiguration } from "@agtech/web/components/Grids/AgTechGridHeader";
import { AgTechClickableIcon } from "@agtech/web/components/Icons/AgTechIcons";
import { AgTechMoneyFormatter } from "@agtech/web/utilities/AgTechUtilities";
import dayjs from "dayjs";
import ContactCreditDialog from "../../../contacts/dialogs/credits/SaleContactCreditDialog";
import { AgTechGridEntitySelectionIndex, mergeGridEntitySelections } from "@agtech/web/components/Grids/AgTechGridEntities";
import { ContactCredit } from "@sales/data/operation/contacts/credits/ContactCreditData";
import { useAgTechQuery, useAgTechQueryResultProjection } from "@agtech/core/data/queries/AgTechDataQueries";
import { SaleContactDataGetContactDetailsQuery } from "@sales/data/operation/contacts/SaleContactDataQueries";
import { IAgTechGridItem } from "@agtech/web/components/Grids/Data/AgTechGridData";

export type InvoiceChangeCreditsGridProps = {
    contactId: number,
    credits: AgTechGridEntitySelectionIndex<ContactCredit>,
    onCreditSelected: (credit: ContactCredit) => Promise<void>,
    onCreditDeselected: (credit: ContactCredit) => Promise<void>,
    header?: AgTechGridHeaderConfiguration
}

const InvoiceChangeCreditsGrid = (props: InvoiceChangeCreditsGridProps) => {
    const gridQuery = useInvoiceCreditsGridQuery(props);

    return (
        <AgTechGrid
            data={{
                itemQuery: gridQuery
            }}
            header={props.header}
            columns={[
                {
                    header: 'Amount',
                    value: c => AgTechMoneyFormatter.format(c.entity.creditAmount),
                },
                {
                    header: 'Description',
                    value: c => c.entity.description,
                    width: '250px'
                },
                {
                    header: 'Expires',
                    value: c => c.entity.expires ? dayjs(c.entity.expires).format('MM/DD/YYYY') : "N/A",
                }
            ]}
            isRowEntityTheSame={(prev, curr) => {
                return prev.entity.creditAmount === curr.entity.creditAmount &&
                    prev.entity.description === curr.entity.description &&
                    prev.entity.expires === curr.entity.expires;
            }}
            selectionConfig={{
                isItemSelected: credit => credit.isSelected,
                onItemSelected: async selectedCredit => props.onCreditSelected(selectedCredit.entity),
                onItemDeSelected: async deselectedCredit => props.onCreditDeselected(deselectedCredit.entity)
            }}
            actions={{
                rendering: creditSelection => {
                    return (
                        <AgTechClickableIcon
                            icon='bi bi-pencil-square'
                            tooltip="Update credit"
                            onClick={e => ContactCreditDialog.Open(e, {
                                contactId: creditSelection.entity.contactId,
                                creditId: creditSelection.entity.id,
                            })}
                        />
                    )
                }
            }}
            noRecordsConfig={{
                message: 'No credits available',
                action: {
                    prompt: 'To create a credit, click the button below',
                    button: () => (
                        <ContactCreditDialog.Button
                            contactId={props.contactId}
                            classes="button-highlight"
                        />
                    )
                }
            }}
        />
    )
}

const useInvoiceCreditsGridQuery = (props: InvoiceChangeCreditsGridProps) => {
    const gridQuery = useAgTechQuery({
        query: SaleContactDataGetContactDetailsQuery,
        queryContext: { contactId: props.contactId }
    });

    return useAgTechQueryResultProjection({
        queryRun: gridQuery,
        projection: c => {
            return {
                items: mergeGridEntitySelections<ContactCredit>({
                    entitySelections: props.credits,
                    entities: Object.values(c.credits),
                }),
                context: {
                    availableCredits: Object.values(c.credits)
                }
            }
        }
    });
}

export default InvoiceChangeCreditsGrid;